import React from 'react'
import PropTypes from 'prop-types'
import { Ratio } from 'react-bootstrap'
import dynamic from 'next/dynamic'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

import fantasyStyles from './style.module.scss'
import { checkIsGlanceView, dateCheck } from '@shared/utils'
import telegram from '@assets/images/icon/telegram-share.png'
import { DownloadIcon, WarningIcon } from '@shared-components/ctIcons'
import { TELEGRAM_NEWS_LINK } from '@shared/constants'

const TableOfContent = dynamic(() => import('@shared-components/fantasyTips/tableOfContent'))
const InfoBlock = dynamic(() => import('@shared-components/fantasyTips/infoBlock'))
const MatchStats = dynamic(() => import('@shared/components/fantasyTips/matchStats'))
const WeatherReport = dynamic(() => import('@shared-components/fantasyTips/weatherReport'))
const WinPrediction = dynamic(() => import('@shared-components/fantasyTips/winPrediction'))
const MatchInfo = dynamic(() => import('@shared-components/fantasyTips/matchInfo'))
const PlayerPicks = dynamic(() => import('@shared-components/fantasyTips/playerPicks'))
const PlayingXI = dynamic(() => import('@shared-components/fantasyTips/playingXI'))
const AvoidPlayer = dynamic(() => import('@shared-components/fantasyTips/avoidPlayer'))
// const MatchProbability = dynamic(() => import('@shared-components/match/matchProbability'))
const FantasyTeam = dynamic(() => import('@shared-components/fantasyTips/fantasyTeam'))
const TipsNote = dynamic(() => import('@shared-components/fantasyTips/tipsNote'))
const PitchReport = dynamic(() => import('@shared-components/fantasyTips/pitchReport'))
// const HeadToHead = dynamic(() => import('@shared-components/fantasyTips/headToHead'))
const TeamForm = dynamic(() => import('@shared-components/fantasyTips/teamForm'))

// const LatestMatches = dynamic(() => import('@shared-components/fantasyTips/latestMatches'))
const Ads = dynamic(() => import('@shared/components/ads'))
const GlanceAd = dynamic(() => import('@shared/components/ads/glanceAd'))
const CustomLink = dynamic(() => import('@shared/components/customLink'))
const FollowUs = dynamic(() => import('@shared/components/articleDetail/followUs'))
const OnMouseAndScroll = dynamic(() => import('@shared/components/lazyLoad/onMouseAndScroll'))
// const CricketPedia = dynamic(() => import('@shared/components/widgets/cricketPedia'))

function FantasyArticleContent({ data }) {
  const router = useRouter()
  const isMobileWebView = router?.query?.isMobileWebView
  const isGlanceView = checkIsGlanceView(router?.query)
  const { t } = useTranslation()
  const fantasyArticleData = data
  const playerTeam = {
    [data?.oOverview?.oTeam1?.oTeam?._id]: data?.oOverview?.oTeam1?.oTeam,
    [data?.oOverview?.oTeam2?.oTeam?._id]: data?.oOverview?.oTeam2?.oTeam
  }

  return (
    <div className={fantasyStyles.fantasySection}>
      <TableOfContent fantasystyles={fantasyStyles} data={fantasyArticleData} />
      {fantasyArticleData?.sMatchPreview !== null && (
        <InfoBlock fantasystyles={fantasyStyles} title={t('common:Preview')} info={fantasyArticleData?.aMatchPreview} article={data} />
      )}
      {isGlanceView && (
        <GlanceAd
          id="div-gpt-ad-5"
          adId="Crictracker_mrec_mid"
          dimension={[[300, 250], [336, 280], 'fluid']}
          adUnitName="Crictracker_English_InArticleMedium_Mid2"
          placementName="InArticleMedium"
          className="d-flex justify-content-center"
          width={300}
          height={250}
          pageName="crictracker.com"
        />
      )}
      {!isMobileWebView && !isGlanceView && (
        <Ads
          id="div-ad-gpt-138639789-1646637168-0"
          adIdDesktop="Crictracker_New_Desktop_AP_MID_728x90"
          adIdMobile="Crictracker_New_Mobile_AP_MID1_300x250"
          dimensionDesktop={[728, 90]}
          dimensionMobile={[300, 250]}
          slotId="23148549803"
          mobile
          className={'text-center mt-4 mb-4'}
        />
      )}
      {data?.sVideoUrl && (
        <section className="common-section pb-0">
          <Ratio aspectRatio="16x9" className={`${fantasyStyles.iframeVideo} m-auto br-lg overflow-hidden`}>
            <iframe
              width="560"
              height="315"
              src={data?.sVideoUrl}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </Ratio>
        </section>
      )}
      {isMobileWebView && <br />}
      {(fantasyArticleData?.oOverview?.sBroadCastingPlatform || fantasyArticleData?.oMatch || fantasyArticleData?.oOverview) && (
        <MatchInfo
          fantasystyles={fantasyStyles}
          broadcasting={fantasyArticleData?.oOverview?.sBroadCastingPlatform}
          matchData={fantasyArticleData?.oMatch}
          fantasyOverview={fantasyArticleData?.oOverview}
        />
      )}
      {isMobileWebView && (
        <Ads
          id="div-MID1-app-ad"
          adIdDesktop="crictracker_new_mobile_webapp_ap_mid_300x250"
          slotId="23148549803"
          dimensionDesktop={[300, 250]}
          className="text-center mt-2"
        />
      )}
      {fantasyArticleData?.WeatherReport?.data?.getWeatherCondition ? (
        <WeatherReport fantasystyles={fantasyStyles} fantasyArticleData={fantasyArticleData} />
      ) : null}
      {fantasyArticleData?.oAdvanceFeature?.bTeamForm && <TeamForm fantasyArticleData={data} />}
      {/* <HeadToHead /> */}
      {fantasyArticleData?.oAdvanceFeature?.bPlayerStats && (
        <OnMouseAndScroll>
          <MatchStats fantasyStyles={fantasyStyles} fantasyArticleData={data} playerTeam={playerTeam} />
        </OnMouseAndScroll>
      )}
      {!isMobileWebView && !isGlanceView && (
        <Ads
          id="div-ad-gpt-138639789-1646637168-01"
          adIdDesktop="Crictracker_New_Desktop_AP_MID_728x90"
          adIdMobile="Crictracker_New_Mobile_AP_MID1_300x250"
          dimensionDesktop={[728, 90]}
          dimensionMobile={[300, 250]}
          slotId="23148549803"
          mobile
          className={'text-center'}
        />
      )}
      {/* <CricketPedia className='d-md-none mb-0 mt-1' /> */}
      {fantasyArticleData?.oOverview !== null && (
        <>
          <PlayingXI fantasystyles={fantasyStyles} probableXI={fantasyArticleData?.oOverview} />
          {isMobileWebView && (
            <Ads
              id="div-MID2-app-ad"
              adIdDesktop="crictracker_new_mobile_webapp_ap_mid_300x250"
              slotId="23148549803"
              dimensionDesktop={[300, 250]}
              className="text-center mt-2"
            />
          )}
          <InfoBlock
            fantasystyles={fantasyStyles}
            title={t('common:InjuryAndAvailabilityNews')}
            info={fantasyArticleData?.oOverview?.sNews}
            id={'availabilityNews'}
          />
        </>
      )}
      {/* <LatestMatches fantasystyles={fantasyStyles} /> */}
      {fantasyArticleData?.overViewData?.oWinnerTeam?.sTitle && <WinPrediction overViewData={fantasyArticleData?.oOverview} />}
      {!isMobileWebView && !isGlanceView && (
        <>
          <Ads
            id="div-ad-gpt-23148549803-1728886625-3"
            adIdDesktop="Crictracker_New_Desktop_AP_BTF_728x90"
            adIdMobile="Crictracker_New_Mobile_AP_ATF_300x250"
            dimensionDesktop={[728, 90]}
            dimensionMobile={[300, 250]}
            slotId="23148549803"
            mobile
            className={'text-center mt-2'}
          />
        </>
      )}
      {fantasyArticleData?.oAdvanceFeature?.bPitchReport && fantasyArticleData?.oOverview !== null && (
        <PitchReport fantasystyles={fantasyStyles} title={t('common:PitchReport')} overviewData={fantasyArticleData?.oOverview} />
      )}
      <PlayerPicks playerTeam={playerTeam} fantasystyles={fantasyStyles} playerPicksData={fantasyArticleData} />
      {!isMobileWebView && !isGlanceView && (
        <Ads
          id="div-ad-gpt-138639789-1646637168-02"
          adIdDesktop="Crictracker_New_Desktop_AP_MID_728x90"
          adIdMobile="Crictracker_New_Mobile_AP_MID1_300x250"
          dimensionDesktop={[728, 90]}
          dimensionMobile={[300, 250]}
          slotId="23148549803"
          mobile
          className={'text-center mt-2'}
        />
      )}
      {fantasyArticleData?.aAvoidPlayerFan?.length > 0 && (
        <AvoidPlayer
          fantasystyles={fantasyStyles}
          avoidPlayers={fantasyArticleData?.aAvoidPlayerFan}
          platFormType={fantasyArticleData?.ePlatformType}
          playerTeam={playerTeam}
          // playerData={playerData}
        />
      )}
      {fantasyArticleData?.sMustPick && (
        <InfoBlock
          fantasystyles={fantasyStyles}
          title={t('common:MustPickForFantasy')}
          info={fantasyArticleData?.aMustPick}
          article={data}
        />
      )}
      {fantasyArticleData?.aLeague?.[0]?.eLeague && (
        <FantasyTeam
          playerTeam={playerTeam}
          fantasystyles={fantasyStyles}
          type={fantasyArticleData?.ePlatformType}
          teamData={fantasyArticleData?.aLeague}
          matchData={fantasyArticleData?.oMatch}
          updatedTime={dateCheck(data?.dModifiedDate || data?.dUpdated)}
        />
      )}
      {fantasyArticleData?.oOtherInfo?.sExpertAdvice && (
        <InfoBlock
          fantasystyles={fantasyStyles}
          title={t('common:ExpertAdvice')}
          info={fantasyArticleData?.oOtherInfo?.sExpertAdvice}
          id={'expertAdvice'}
        />
      )}
      <FollowUs className="mt-3 mt-lg-4 mb-1" />
      <TipsNote
        heading={t('common:Note')}
        linkImage={telegram}
        linkText={t('common:Telegram')}
        link={TELEGRAM_NEWS_LINK}
        descText={t('common:InformationOnTelegram')}
        isBgPrimary
      />
      <TipsNote heading={t('common:Disclaimer')} headingIcon={<WarningIcon />} descText={t('common:OwnDecisionNote')} />
      {!isMobileWebView && <TipsNote heading={t('common:DownloadOurApp')} headingIcon={<DownloadIcon />} isDownloadAppDisclaimer />}
      <div className={`${fantasyStyles.tagList} d-flex flex-lg-wrap pb-2 pb-lg-0 mt-2 mt-sm-3 mx-n1 overflow-auto`}>
        {fantasyArticleData?.aSeriesCategory?.map((cat) => (
          <CustomLink
            key={cat?.oSeo?.sSlug}
            target="_blank"
            className="badge bg-primary m-1 font-semi py-1 px-3"
            href={'/' + cat?.oSeo?.sSlug}
            prefetch={false}
          >
            {cat?.sName}
          </CustomLink>
        ))}
        {fantasyArticleData?.aTeamTag?.map((team) => (
          <CustomLink
            target="_blank"
            className="badge bg-primary m-1 font-semi py-1 px-3"
            key={team?.oSeo?.sSlug}
            href={'/' + team?.oSeo?.sSlug}
            prefetch={false}
          >
            {team?.sName}
          </CustomLink>
        ))}
        {fantasyArticleData?.aPlayerTag?.map((player) => (
          <CustomLink
            target="_blank"
            className="badge bg-primary m-1 font-semi py-1 px-3"
            key={player?.oSeo?.sSlug}
            href={'/' + player?.oSeo?.sSlug}
            prefetch={false}
          >
            {player?.sName}
          </CustomLink>
        ))}
        {fantasyArticleData?.aTagsData?.map((tag) => (
          <CustomLink
            target="_blank"
            className="badge bg-primary m-1 font-semi py-1 px-3"
            key={tag?.oSeo?.sSlug}
            href={'/' + tag?.oSeo?.sSlug}
            prefetch={false}
          >
            {tag?.sName}
          </CustomLink>
        ))}
      </div>
    </div>
  )
}

FantasyArticleContent.propTypes = {
  data: PropTypes.object,
  playerData: PropTypes.array
}

export default FantasyArticleContent
